"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  AlertCircle,
  Award,
  Baby,
  Calendar,
  CheckCircle,
  Heart,
  MapPin,
  Star,
  Stethoscope,
  TrendingUp,
  Users,
  Weight,
} from "lucide-react";
import {
  Animal,
  RecipientInput,
  ReproductiveProcessData,
} from "./AnimalColumns";
import { PedigreeTree } from "@/features/Animals/Pedigree.tsx";
import { Image } from "@heroui/image";
import logo from "/public/logo.png";

interface ReproductiveProcessTableProps {
  data: ReproductiveProcessData[];
  className?: string;
}

export function ReproductiveProcessTable({
  data,
  className = "",
}: ReproductiveProcessTableProps) {
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "positive" || statusLower === "confirmed") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          {status}
        </Badge>
      );
    } else if (statusLower === "negative") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          {status}
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200">
        {status}
      </Badge>
    );
  };

  const getEvaluationBadge = (evaluation: string) => {
    const evalLower = evaluation.toLowerCase();
    if (evalLower === "good" || evalLower === "excellent") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          {evaluation}
        </Badge>
      );
    } else if (evalLower === "fair" || evalLower === "average") {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          {evaluation}
        </Badge>
      );
    } else if (evalLower === "poor") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          {evaluation}
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200">
        {evaluation || "N/A"}
      </Badge>
    );
  };

  const getTypeColor = (type: string) => {
    const typeLower = type.toLowerCase();
    if (typeLower === "ai") {
      return "bg-blue-100 text-blue-800 border-blue-200";
    } else if (typeLower === "et") {
      return "bg-purple-100 text-purple-800 border-purple-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    return String(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "") return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (!data || data.length === 0) {
    return (
      <Card
        className={`w-full bg-white/80 backdrop-blur-sm border-white/20 shadow-xl ${className}`}
      >
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-indigo-900">
            <Heart className="w-5 h-5" />
            Reproductive Process
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">
              No reproductive process data available
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`w-full bg-white/80 backdrop-blur-sm border-white/20 shadow-xl ${className}`}
    >
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-indigo-900">
          <Heart className="w-5 h-5" />
          Reproductive Process ({data.length}{" "}
          {data.length === 1 ? "Record" : "Records"})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="">
          <Table className="w-screen overflow-x-auto">
            <TableHeader>
              <TableRow className="bg-gray-50/50">
                <TableHead className="font-semibold">Cow Details</TableHead>
                <TableHead className="font-semibold">Synchronization</TableHead>
                <TableHead className="font-semibold">AI/ET Process</TableHead>
                <TableHead className="font-semibold">
                  Pregnancy Results
                </TableHead>
                <TableHead className="font-semibold">Calf Birth Info</TableHead>
                <TableHead className="font-semibold">Calf Physical</TableHead>
                <TableHead className="font-semibold">Evaluations</TableHead>
                <TableHead className="font-semibold">
                  Health & Weaning
                </TableHead>
                <TableHead className="font-semibold">
                  Donor Animal Info
                </TableHead>
                <TableHead className="font-semibold">
                  Submission & Verification
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((record: any, index: any) => (
                <TableRow
                  key={record.id || index}
                  className="hover:bg-indigo-50/50 transition-colors"
                >
                  {/* Cow Details */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-1">
                      <p className="font-medium text-indigo-900">
                        Tag: {formatValue(record.cowEarTag)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Branding: {formatValue(record.branding)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Management: {formatValue(record.cowManagement)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Breed: {formatValue(record.cowBreed)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth Date: {formatDate(record.cowBirthDate)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Synchronization */}
                  <TableCell className="min-w-[200px]">
                    <div className="space-y-1">
                      <p className="font-medium text-blue-900">
                        {formatValue(record.syncMonth)}{" "}
                        {formatValue(record.syncYear)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Batch: {formatValue(record.syncBatch)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Removal: {formatDate(record.synchRemoval)}
                      </p>
                    </div>
                  </TableCell>

                  {/* AI/ET Process */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-2">
                      <Badge className={getTypeColor(record.aiOrETType)}>
                        {formatValue(record.aiOrETType).toUpperCase()}
                      </Badge>
                      <p className="text-sm text-gray-600">
                        Date AI/ET: {formatDate(record.dateAIET)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Bull: {formatValue(record.bull)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Donor Cow: {formatValue(record.donorCow)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Pregnancy Results */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Check 1:</p>
                        {getStatusBadge(record.pregnancyCheck1)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">
                          Final Result:
                        </p>
                        {getStatusBadge(record.finalPregnancyResult)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Final Check Date:{" "}
                        {formatDate(record.finalPregnancyCheckDate)}
                      </p>
                      {record.finalPregnancyNotes && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Notes:</p>
                          <p className="text-xs text-gray-500">
                            {record.finalPregnancyNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  </TableCell>

                  {/* Calf Birth Info */}
                  <TableCell className="min-w-[200px]">
                    <div className="space-y-1">
                      <p className="font-medium text-green-900">
                        Number: {formatValue(record.calfNumber)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth Date: {formatDate(record.calfBirthDate)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth: {formatValue(record.calfBirthMonth)}{" "}
                        {formatValue(record.calfBirthYear)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Sex: {formatValue(record.calfSex)}
                      </p>
                      <Badge className={getTypeColor(record.calfType)}>
                        {formatValue(record.calfType).toUpperCase()}
                      </Badge>
                      <p className="text-sm text-gray-600">
                        Gestation: {formatValue(record.gestationPeriod)} days
                      </p>
                      {record.calfBirthNotes && (
                        <p className="text-xs text-gray-500 mt-1">
                          Notes: {record.calfBirthNotes}
                        </p>
                      )}
                    </div>
                  </TableCell>

                  {/* Calf Physical */}
                  <TableCell className="min-w-[180px]">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">
                        Coat Color: {formatValue(record.calfCoatColor)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth Weight: {formatValue(record.calfBirthWeight)} kg
                      </p>
                      <p className="text-sm text-gray-600">
                        Color: {formatValue(record.color)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Umbilical: {formatValue(record.umbilicalCondition)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Evaluations */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">Head:</p>
                        {getEvaluationBadge(record.headEvaluation)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Body Type:</p>
                        {getEvaluationBadge(record.bodyType)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Muscle:</p>
                        {getEvaluationBadge(record.muscleDevelopment)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Structure:</p>
                        {getEvaluationBadge(record.bodyStructure)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Conformation:</p>
                        {getEvaluationBadge(record.generalConformation)}
                      </div>
                    </div>
                  </TableCell>

                  {/* Health & Weaning */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">
                        Weaning Date: {formatDate(record.weaningDate)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Weaning Weight: {formatValue(record.weaningWeight)} kg
                      </p>
                      <p className="text-sm text-gray-600">
                        Weaning Location: {formatValue(record.weaningLocation)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Tick Count: {formatValue(record.tickCount)}
                      </p>

                      {record.vaccinations &&
                        record.vaccinations.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-600 mb-1">
                              Vaccinations:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {record.vaccinations.map(
                                (vaccine: any, idx: any) => (
                                  <Badge
                                    key={idx}
                                    className="bg-green-100 text-green-800 border-green-200 text-xs"
                                  >
                                    {vaccine.toUpperCase()}
                                  </Badge>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {(record.calfDeathDate || record.calfDeathNotes) && (
                        <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs text-red-800 font-medium">
                            Death Record:
                          </p>
                          {record.calfDeathDate && (
                            <p className="text-xs text-red-600">
                              Date: {formatDate(record.calfDeathDate)}
                            </p>
                          )}
                          {record.calfDeathNotes && (
                            <p className="text-xs text-red-600">
                              Notes: {record.calfDeathNotes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>

                  {/* Donor Animal */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">
                        <b>Recipient Cow DGR :</b>{" "}
                        {formatDate(record.dgrRecipientCow)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Weight:</b> {formatValue(record.donorWeight)} kg
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Bcs D0:</b> {formatValue(record.bcsD0)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Donor Cow:</b> {formatValue(record.donorCowAnimal)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Donor Breed:</b> {formatValue(record.donorBreed)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Bull Name:</b> {formatValue(record.bullName)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Bull DGR:</b> {formatValue(record.dgrBull)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Donor Breed Second:</b>
                        {formatValue(record.donorBreedSecond)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <b>Embryo Breed:</b> {formatValue(record.embryoBreed)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Submission & Verification */}
                  <TableCell className="min-w-[180px]">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">Submission:</p>
                      <p className="text-xs text-gray-500">
                        {formatValue(record.submissionTime)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Tag Verified: {formatValue(record.earTagVerified)}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {formatValue(record.id)}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

interface RecipientEvaluationTableProps {
  data: RecipientInput[];
  className?: string;
}

export function RecipientEvaluationTable({
  data,
  className = "",
}: RecipientEvaluationTableProps) {
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "confirmed" || statusLower === "positive") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          {status}
        </Badge>
      );
    } else if (statusLower === "negative") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          {status}
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200">
        {status}
      </Badge>
    );
  };

  const getEvaluationBadge = (evaluation: string) => {
    const evalLower = evaluation.toLowerCase();
    if (evalLower === "good" || evalLower === "excellent") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          {evaluation}
        </Badge>
      );
    } else if (evalLower === "fair" || evalLower === "average") {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          {evaluation}
        </Badge>
      );
    } else if (evalLower === "poor") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          {evaluation}
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 border-gray-200">
        {evaluation || "N/A"}
      </Badge>
    );
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    return String(value);
  };

  if (!data || data.length === 0) {
    return (
      <Card
        className={`w-full bg-white/80 backdrop-blur-sm border-white/20 shadow-xl ${className}`}
      >
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-pink-900">
            <Baby className="w-5 h-5" />
            Recipient Evaluation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">
              No recipient evaluation data available
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`w-full overflow-hidden bg-white/80 backdrop-blur-sm border-white/20 shadow-xl ${className}`}
    >
      <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-pink-900">
          <Baby className="w-5 h-5" />
          Recipient Evaluation ({data.length}{" "}
          {data.length === 1 ? "Record" : "Records"})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="">
          <Table className="w-screen overflow-x-auto">
            <TableHeader>
              <TableRow className="bg-gray-50/50">
                <TableHead className="font-semibold">Cow Information</TableHead>
                <TableHead className="font-semibold">Synchronization</TableHead>
                <TableHead className="font-semibold">
                  Pregnancy Status
                </TableHead>
                <TableHead className="font-semibold">
                  Calf Birth Details
                </TableHead>
                <TableHead className="font-semibold">Calf Physical</TableHead>
                <TableHead className="font-semibold">
                  Calf Evaluations
                </TableHead>
                <TableHead className="font-semibold">
                  Weaning & Health
                </TableHead>
                <TableHead className="font-semibold">Additional Info</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((record, index) => (
                <TableRow
                  key={index}
                  className="hover:bg-pink-50/50 transition-colors"
                >
                  {/* Cow Information */}
                  <TableCell className="min-w-[200px]">
                    <div className="space-y-1">
                      <p className="font-medium text-pink-900">
                        Tag: {formatValue(record.cowEarTag)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Management: {formatValue(record.cowManagement)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Breed: {formatValue(record.cowBreed)}
                      </p>
                      <p className="text-sm text-gray-600">
                        BCS: {formatValue(record.bcs)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Synchronization */}
                  <TableCell className="min-w-[180px]">
                    <div className="space-y-1">
                      <p className="font-medium text-blue-900">
                        {formatValue(record.syncMonth)}{" "}
                        {formatValue(record.syncYear)}
                      </p>
                      <p className="text-sm text-gray-600">Estrous Cycle:</p>
                      <p className="text-xs text-gray-500">
                        {formatValue(record.estrousCycle)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Pregnancy Status */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Diagnosis:</p>
                        {getStatusBadge(record.pregnancyDiagnosis)}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Final Check:
                        </p>
                        {getStatusBadge(record.finalPregnancyCheck)}
                      </div>
                      {record.observationFinalPregnancyCheck && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            Observation:
                          </p>
                          <p className="text-xs text-gray-500">
                            {record.observationFinalPregnancyCheck}
                          </p>
                        </div>
                      )}
                    </div>
                  </TableCell>

                  {/* Calf Birth Details */}
                  <TableCell className="min-w-[200px]">
                    <div className="space-y-1">
                      <p className="font-medium text-green-900">
                        Number: {formatValue(record.calfNumber)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth Date: {formatValue(record.calfBirthDate)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth: {formatValue(record.calfBirthMonth)}{" "}
                        {formatValue(record.calfBirthYear)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Type: {formatValue(record.calfType)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Gestation: {formatValue(record.gestationPeriod)} days
                      </p>
                      {record.calfBirthNotes && (
                        <p className="text-xs text-gray-500 mt-1">
                          Notes: {record.calfBirthNotes}
                        </p>
                      )}
                    </div>
                  </TableCell>

                  {/* Calf Physical */}
                  <TableCell className="min-w-[180px]">
                    <div className="space-y-1">
                      <p className="font-medium text-purple-900">
                        Sex: {formatValue(record.calfSex)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Coat Color: {formatValue(record.calfCoatColor)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Birth Weight: {formatValue(record.calfBirthWeight)} kg
                      </p>
                      <p className="text-sm text-gray-600">
                        Color: {formatValue(record.color)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Umbilical: {formatValue(record.umbilicalCondition)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Calf Evaluations */}
                  <TableCell className="min-w-[220px]">
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-600">
                          Head Evaluation:
                        </p>
                        {getEvaluationBadge(record.headEvaluation)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Body Type:</p>
                        {getEvaluationBadge(record.bodyType)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">
                          Muscle Development:
                        </p>
                        {getEvaluationBadge(record.muscleDevelopment)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Body Structure:</p>
                        {getEvaluationBadge(record.bodyStructure)}
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">
                          General Conformation:
                        </p>
                        {/* {getEvaluationBadge(record.generalConformation)} */}
                      </div>
                    </div>
                  </TableCell>

                  {/* Weaning & Health */}
                  <TableCell className="min-w-[200px]">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">
                        Weaning Date: {formatValue(record.weaningDate)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Weaning Weight: {formatValue(record.weaningWeight)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Weaning Location: {formatValue(record.weaningLocation)}
                      </p>
                      <p className="text-sm text-gray-600">
                        Tick Count: {formatValue(record.tickCount)}
                      </p>
                      {record.calfDeathDate && (
                        <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs text-red-800 font-medium">
                            Death Date:
                          </p>
                          <p className="text-xs text-red-600">
                            {formatValue(record.calfDeathDate)}
                          </p>
                          {record.calfDeathNotes && (
                            <p className="text-xs text-red-600">
                              Notes: {record.calfDeathNotes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>

                  {/* Additional Info */}
                  <TableCell className="min-w-[150px]">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1">
                        {record.earTagVerified ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className="text-xs text-gray-600">
                          Tag Verified
                        </span>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export function FarmerAnimalProfile({
  animalData,
  className = "",
}: {
  animalData: Animal;
  className: string;
}) {
  if (!animalData) {
    return <span>NO Animal Data</span>;
  }
  return (
    <div
      className={`min-h-screen w-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 ${className}`}
    >
      <div
        className={"w-full flex flex-col items-center justify-between gap-10"}
      >
        {/* <DualLogoShowcase firstLogoSrc={logo} size="md" /> */}
        <img alt="Logo" className="h-16 z-20 " src={logo} />
      </div>
      <div className="max-w-7xl mx-auto flex flex-col gap-10">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 p-8 mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white/30 shadow-xl">
                <img
                  src={
                    animalData?.image ?? "/placeholder.svg?height=128&width=128"
                  }
                  alt={animalData?.name ?? "N/A"}
                  className="w-full h-full object-cover mx-auto"
                />
              </div>
              <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-2 shadow-lg">
                <Star className="w-5 h-5 text-yellow-800" />
              </div>
            </div>

            <div className="flex-1 text-white">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold">
                  {animalData?.name ?? "N/A"}
                </h1>
                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                  {animalData?.cattleClass ?? "N/A"}
                </Badge>
              </div>
              <p className="text-xl text-white/90 mb-4">
                DGR: {animalData?.gn || animalData?.dgr || "N/A"}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm opacity-90">Age</span>
                  </div>
                  <p className="font-semibold">{animalData.age}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4" />
                    <span className="text-sm opacity-90">Weight</span>
                  </div>
                  <p className="font-semibold">{animalData.weight}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm opacity-90">ADG</span>
                  </div>
                  <p className="font-semibold">{animalData.adg ?? "N/A"}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4" />
                    <span className="text-sm opacity-90">Efficiency</span>
                  </div>
                  <p className="font-semibold">
                    {animalData.indexEfficiency ?? "N/A"}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Basic Information */}
        <div className="relative flex flex-col items-center justify-center mx-auto aspect-video rounded-xl overflow-hidden shadow-lg">
          <Image
            src={animalData?.image || "/placeholder.svg?height=400&width=600"}
            alt={`${animalData?.name} - Image`}
            className="object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
        <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Activity className="w-5 h-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Sex
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData.sex}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Birth Date
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData.birthDate}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Coat Color
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData.coatColor}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Size
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData.size}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-orange-900">
              <Heart className="w-5 h-5" />
              Breeding Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Paternal Nucleus
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.paternalNucleus ?? "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Paternal Type
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.paternalType ?? "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Breeding Type
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.breedingType ?? "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Racial Composition
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.racialComposition ?? "N/A"}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Vaccination
                  </label>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    {animalData?.vaccination ?? "N/A"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Birth Condition
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.birthCondition ?? "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Insemination Program
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.inseminationProgram ?? "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Phases
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {/* {animalData?.phases ?? "N/A"} */}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Evaluation
                  </label>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                    {animalData?.evaluation ?? "N/A"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Diagnosis
                  </label>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    {animalData?.diagnosis ?? "N/A"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Status
                  </label>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                    {animalData?.status ?? "N/A"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Slaughter Info
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {animalData?.slaughterInfo ?? "N/A"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"></div>
            </div>
          </CardContent>
        </Card>
        {/* Management Information */}
        {/* Farm Information */}
        <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-yellow-900">
              <MapPin className="w-5 h-5" />
              Farm Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Farmer
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {animalData.farm ?? "N/A"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Farmer Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {animalData.farmer ?? "N/A"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Farm Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {animalData?.farm ?? "N/A"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Assigned Supervisor
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {animalData?.assignedSupervisor ?? "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Health & Management */}
        <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-teal-900">
              <Stethoscope className="w-5 h-5" />
              Health & Management
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Cattle Class
                </label>
                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">
                  {animalData?.cattleClass ?? "N/A"}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Current Phase
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {/* {animalData?.phases ?? "N/A"} */}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Health Status
                </label>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  {animalData?.diagnosis ?? "N/A"}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Overall Evaluation
                </label>
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  {animalData?.evaluation ?? "N/A"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Genealogy */}
        <Card className="bg-white/80 backdrop-blur-sm border-white/20 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <Users className="w-5 h-5" />
              Genealogy
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Parents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Parents
                </h3>
                <div className="space-y-4">
                  <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                    <h4 className="font-medium text-pink-900 mb-2">
                      Dam (Mother)
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-medium">Name:</span>{" "}
                        {animalData?.damName ?? "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">DGR:</span>{" "}
                        {animalData?.damDGR ?? "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Breed:</span>{" "}
                        {animalData?.damBreed ?? "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">
                      Sire (Father)
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-medium">Name:</span>{" "}
                        {animalData?.sireName ?? "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">DGR:</span>{" "}
                        {animalData?.sireDGR ?? "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Breed:</span>{" "}
                        {animalData?.sireBreed ?? "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Grandparents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Grandparents
                </h3>
                <div className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2">
                      Paternal Grandparents
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <p className="font-medium">Grand Dam:</p>
                        <p>
                          {animalData?.paternalGrandDamName ?? "N/A"} (
                          {animalData?.paternalGrandDamDGR ?? "N/A"})
                        </p>
                        <p className="text-gray-600">
                          {animalData?.paternalGrandDamBreed ?? "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">Grand Sire:</p>
                        <p>
                          {animalData?.paternalGrandSireName ?? "N/A"} (
                          {animalData?.paternalGrandSireDGR ?? "N/A"})
                        </p>
                        <p className="text-gray-600">
                          {animalData?.paternalGrandSireBreed ?? "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-900 mb-2">
                      Maternal Grandparents
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <p className="font-medium">Grand Dam:</p>
                        <p>
                          {animalData?.maternalGrandDamName ?? "N/A"} (
                          {animalData?.maternalGrandDamDGR ?? "N/A"})
                        </p>
                        <p className="text-gray-600">
                          {animalData?.maternalGrandDamBreed ?? "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">Grand Sire:</p>
                        <p>
                          {animalData?.maternalGrandSireName ?? "N/A"} (
                          {animalData?.maternalGrandSireDGR ?? "N/A"})
                        </p>
                        <p className="text-gray-600">
                          {animalData?.maternalGrandSireBreed ?? "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {
          // @ts-ignore
          <PedigreeTree animal={animalData} />
        }
      </div>
    </div>
  );
}
